import React from 'react';
import {Common, DetectionType} from '@taopix/taopix-core';
import {flipSize, getRotation} from '@taopix/taopix-asset-manager';
import {PageOverlayDetectionDetail} from '@taopix/taopix-core/dist/types/types';
import {DragAndDropActions} from '../Context/DragAndDrop/DragAndDropActionTypes';
import {useDragAndDropState} from '../Context/DragAndDrop/DragAndDropContext';
import {DragAndDropNames} from '../../Enums';
import {useTaopix} from '../Context/TaopixContext';
import {ImageDimension} from '@taopix/taopix-asset-manager/dist/types/types';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';

type DynamicType = {
  [key: string]: any;
  type?: any;
};

interface DragProps {
  draggable?: boolean;
  onItemTouchStart?: (event: React.TouchEvent<HTMLElement>) => void;
  onItemTouchEnd?: (event: React.TouchEvent<HTMLElement>) => void;
  onItemDragStart?: (event: React.DragEvent<HTMLElement>, target: any) => void;
  onItemDragOver?: (event: React.DragEvent<HTMLElement>) => void;
  onItemDragEnd?: (event: React.DragEvent<HTMLElement>) => void;
  onTouchStart?: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd?: (event: React.TouchEvent<HTMLElement>) => void;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: (event: React.DragEvent<HTMLElement>) => void;
}

export type DragAndDropProps = {
  name: DragAndDropNames;
  detectionType: DetectionType;
  item: DynamicType;
  passive?: boolean;
  allowDrag?: boolean;
  children: React.ReactElement | React.ReactElement[];
};

const Drag = ({
  name,
  detectionType,
  item,
  passive = false,
  allowDrag = true,
  children,
}: DragAndDropProps): React.ReactElement => {
  const app = useTaopix();
  const ui = useUiState();
  const dragAndDrop = useDragAndDropState();

  /**
   * Event to do when dragStart event is triggered.
   * Fires the onDragStart CustomEvent.
   *
   * @param event React DragEvent object.
   * @param data Target data.
   */
  const onDragStart = async (event: React.DragEvent<HTMLElement>, data: DynamicType) => {
    dragAndDrop.dispatch(
      DragAndDropActions.setDragData({
        data: data,
        name: name,
      })
    );

    // Required to call setData to make the element draggable.
    event.dataTransfer.setData('text/plain', name);

    const originalTarget = event.target as HTMLImageElement;

    // Get the offsetParent to get correct size, including rotated images.
    // Remove the padding to get the true width & height.
    const style = window.getComputedStyle(originalTarget?.offsetParent as HTMLElement);
    const boundingRect = {
      height:
        parseFloat(style.getPropertyValue('height')) -
        parseFloat(style.getPropertyValue('padding-top')) -
        parseFloat(style.getPropertyValue('padding-bottom')),
      width:
        parseFloat(style.getPropertyValue('width')) -
        parseFloat(style.getPropertyValue('padding-left')) -
        parseFloat(style.getPropertyValue('padding-right')),
    };

    if ('IMG' === originalTarget.nodeName) {
      const img = originalTarget as HTMLImageElement;
      const rotation = data?.rotation || 0;

      // Create a canvas and draw the image into it.
      // This is to get around the image being a blob and potentially not available
      // for a while after the image is first uploaded.
      const canvas = document.createElement('canvas');
      canvas.width = boundingRect.width;
      canvas.height = boundingRect.height;

      const context = canvas.getContext('2d');

      // hide the canvas.
      canvas.classList.add('tpx-drag-canvas');

      // Push the canvas off-screen.
      canvas.style.position = 'absolute';
      canvas.style.left = '-100%';

      // Chrome will only allow canvas drag targets that are in the DOM.
      document.body.append(canvas);

      if (context) {
        const ratio = img.naturalWidth / img.naturalHeight;
        let newWidth = canvas.width;
        let newHeight = canvas.height;
        let xOffset = 0;
        let yOffset = 0;

        if (img.classList.contains('object-cover')) {
          newHeight = newWidth / ratio;

          if (newHeight < canvas.height) {
            newHeight = canvas.height;
            newWidth = newHeight * ratio;
          }

          xOffset = newWidth > canvas.width ? (canvas.width - newWidth) / 2 : 0;
          yOffset = newHeight > canvas.height ? (canvas.height - newHeight) / 2 : 0;
        } else if (img.classList.contains('object-contain')) {
          newHeight = newWidth / ratio;

          if (newHeight > canvas.height) {
            newHeight = canvas.height;
            newWidth = newHeight * ratio;
          }

          xOffset = newWidth < canvas.width ? (canvas.width - newWidth) / 2 : 0;
          yOffset = newHeight < canvas.height ? (canvas.height - newHeight) / 2 : 0;
        } else if (rotation > 0) {
          context.save();

          // Move to the middle of the canvas and rotate from that point.
          context.translate(canvas.width / 2, canvas.height / 2);
          context.rotate(Common.degreesToRadians(rotation * -1));

          // Flip the sizes so the correct variables have the correct size.
          const flippedSize = flipSize(getRotation(rotation), {
            height: newHeight,
            width: newWidth,
          }) as ImageDimension;

          newHeight = flippedSize.height;
          newWidth = flippedSize.width;

          xOffset = -(newWidth / 2);
          yOffset = -(newHeight / 2);
        }

        // Draw the image onto the  canvas.
        context.drawImage(
          img,
          0,
          0,
          // Need to use the natural dimension properties of the image
          // as the height/width is the size of the thumbnail.
          img.naturalWidth,
          img.naturalHeight,
          xOffset,
          yOffset,
          newWidth,
          newHeight
        );

        if (rotation > 0) {
          context.restore();
        }

        // Set the canvas as the drag image.
        event.dataTransfer.setDragImage(canvas, canvas.width / 2, canvas.height / 2);
      }
    }

    const eventDetail: PageOverlayDetectionDetail = {
      type: detectionType,
      event: event as unknown as DragEvent,
      ref: '',
    };

    switch (detectionType) {
      case DetectionType.PageStyle: {
        eventDetail.ref = data.ref;
        break;
      }
      case DetectionType.Background: {
        eventDetail.ref = data.assetRef;
        break;
      }
    }

    app.core.dispatch('PROJECT.DRAG_START', eventDetail).catch(error => {
      console.error(error);
    });
  };

  /**
   * Event to do when dragEnd event is triggered.
   * Fires the onDragEnd CustomEvent.
   *
   * @param event React DragEvent object.
   */
  const onDragEnd = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();

    dragAndDrop.dispatch(DragAndDropActions.clearDragData());
    app.core.dispatch('PROJECT.DRAG_END', {type: detectionType}).catch(error => {
      console.error(error);
    });

    // Remove any drag canvas' that have been added to the DOM.
    document.querySelectorAll('.tpx-drag-canvas').forEach(canvas => {
      if (canvas instanceof HTMLCanvasElement) {
        canvas.height = 0;
        canvas.width = 0;
      }
      canvas.remove();
    });
  };

  /**
   * Event to do when touchstart event is triggered on the draggable element.
   *
   * @param event React DragEvent object.
   */
  const onTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    // Prevents the context menu opening on Android.
    window.oncontextmenu = () => {
      event.preventDefault();
      event.stopPropagation();
      return false;
    };
  };

  /**
   * Event to do when touchend event is triggered on the draggable element.
   *
   * @param event React DragEvent object.
   */
  const onTouchEnd = (event: React.TouchEvent<HTMLElement>) => {
    // Remove the event to re-enable the context menu.
    window.oncontextmenu = () => {};
  };

  const dragTarget = React.Children.map(
    children,
    (child: React.ReactElement): React.ReactElement => {
      const dragProps: DragProps = {};

      if (allowDrag) {
        if (passive) {
          dragProps.onItemTouchStart = onTouchStart;
          dragProps.onItemTouchEnd = onTouchEnd;

          if (!ui.state.isSmallScreen) {
            dragProps.onItemDragStart = onDragStart;
            dragProps.onItemDragEnd = onDragEnd;
          }
        } else {
          dragProps.onTouchStart = (event: React.TouchEvent<HTMLElement>) => onTouchStart(event);
          dragProps.onTouchEnd = (event: React.TouchEvent<HTMLElement>) => onTouchEnd(event);

          if (!ui.state.isSmallScreen) {
            dragProps.draggable = true;
            dragProps.onDragStart = (event: React.DragEvent<HTMLElement>) =>
              onDragStart(event, item);
            dragProps.onDragEnd = (event: React.DragEvent<HTMLElement>) => onDragEnd(event);
          }
        }
      } else {
        dragProps.draggable = false;
      }

      return React.cloneElement(child, {
        ...child.props,
        // webKitUserSelect and WebkitTouchCallout are to prevent the context menu on iOS devices.
        style: {
          ...child.props.style,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        },
        ...dragProps,
      });
    }
  );

  return <>{dragTarget}</>;
};

export default Drag;
